import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Modal,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import withUser from '@components/withUser';
import type { WithUserProps } from '@components/withUser';
import ReservationList from '../components/reservation/ReservationList';
import AwaitingApprovalList from '../components/reservation/AwaitingApprovalList';
import ConfirmCompletedList from '../components/reservation/ConfirmCompletedList';
import QuickReviewModal from '../components/reservation/QuickReviewModal';
import StylistSelector from '@components/StylistSelector';
import { isPast } from 'date-fns';
import {
  getReservations,
  cancelReservation,
  getPlans,
  getBusiness,
  approveReservation,
  noShowReservation,
  completeReservation,
  googleCalendarAuthRedirect,
  listGoogleCalendars,
  syncGoogleEvents,
  syncIOSCalendar,
  saveCalendarsToSync,
  getPendingApprovalReservations
} from '@services/reservationService';
import type { Reservation } from '@app-types/models/reservation';
import type { Stylist } from '@app-types/models/stylist';
import type { Plan } from '@app-types/models/plan';
import type { Business } from '@app-types/models/business';
import './ReservationsPage.scss';
import { useTranslation } from 'react-i18next';
import CalendarSelectionModal from '../components/reservation/CalendarSelectionModal';
import { format } from 'date-fns';
import NoStylist from '@components/NoStylist';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import IOSCalendarUrlDialog from '../components/reservation/IOSCalendarUrlDialog';
import { updateStylistDetails, getStylists } from '@services/stylistService';
import { toast } from 'react-toastify';
import EventIcon from '@mui/icons-material/Event';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import SyncIcon from '@mui/icons-material/Sync';
import PolicyIcon from '@mui/icons-material/Policy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { PageContainer, PageHeader, SectionCard, EmptyState, LoadingState } from '../components/ui';
import { brand } from '../theme/tokens';

interface GoogleCalendar {
  id: string;
  summary: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      remainingSeconds?: number;
    };
  };
  message?: string;
}

type ReservationsPageProps = WithUserProps;

const ReservationsPage: React.FC<ReservationsPageProps> = ({ user: _user }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [pendingApprovalReservations, setPendingApprovalReservations] = useState<Reservation[]>([]);
  const [pastIncompleteReservations, setPastIncompleteReservations] = useState<Reservation[]>([]);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [business, setBusiness] = useState<Business | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState<boolean>(false);
  const [reservationToCancel, setReservationToCancel] = useState<string | null>(null);
  const [selectedStylist, setSelectedStylist] = useState<Stylist | null>(null);
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [showPolicyModal, setShowPolicyModal] = useState<boolean>(false);
  const { filter: routeFilter, stylistId: routeStylistId } = useParams<{ filter?: string; stylistId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // This page serves both /reservations/:stylistId/:filter? and the classic
  // mirror /classic/reservations; build sub-paths from the live base so the
  // classic page doesn't bounce the user back to the v2 dashboard.
  const basePath = location.pathname.split('/').slice(0, routeStylistId ? -1 : undefined).join('/') || '/reservations';
  const [showIOSCalendarDialog, setShowIOSCalendarDialog] = useState<boolean>(false);
  const [iosCalendarUrlLoading, setIOSCalendarUrlLoading] = useState<boolean>(false);
  const [iosCalendarUrlError, setIOSCalendarUrlError] = useState<string>('');
  const [iosCalendarUrlSuccess, setIOSCalendarUrlSuccess] = useState<string>('');
  const [iosSyncLoading, setIOSSyncLoading] = useState<boolean>(false);
  const [showReservationModeConfirmModal, setShowReservationModeConfirmModal] = useState<boolean>(false);
  const [newReservationMode, setNewReservationMode] = useState<string>('');
  const [reviewQrReservation, setReviewQrReservation] = useState<{ _id: string; customerName?: string } | null>(null);

  // Get policy URL based on current language
  const getPolicyUrl = (): string => {
    const currentLang = i18n.language;
    return currentLang === 'zh' || currentLang === 'zh-HK' ?
      'https://cms.hkstylist.com/%e6%94%b9%e6%9c%9f%e8%88%87%e5%8f%96%e6%b6%88%e6%94%bf%e7%ad%96/' :
      'https://cms.hkstylist.com/rescheduling-and-cancellations-policy/';
  };

  const fetchReservations = useCallback(async (): Promise<void> => {
    try {
      const response = await getReservations({});
      setReservations(Array.isArray(response.data) ? response.data : []);
    } catch (error: unknown) {
      console.error('Error fetching reservations:', error);
      setError(t('reservationsPage.errorFetchingReservations'));
    }
  }, [t]);

  const fetchStylistReservations = useCallback(async (): Promise<void> => {
    try {
      // Use the dedicated stylist-list endpoint so stylists with no reservations
      // still appear (the legacy /business/stylist-reservations returns reservations,
      // not stylists, which made the page show "no stylists" when the user had
      // stylists but no bookings).
      const stylistData = await getStylists();
      setStylists(Array.isArray(stylistData) ? stylistData : []);
      setStylists(stylistData);
      setSelectedStylist(prev => {
        const currentSelectedId = prev?._id;
        const newSelectedStylist = stylistData.find((s: Stylist) => s._id === currentSelectedId);
        return newSelectedStylist || (stylistData.length > 0 ? stylistData[0] : null);
      });
    } catch (error: unknown) {
      console.error('Error fetching stylists:', error);
      setError(t('reservationsPage.errorFetchingStylists'));
    }
  }, [t, setSelectedStylist, setStylists, setError]);

  const fetchPlans = useCallback(async (): Promise<void> => {
    try {
      const response = await getPlans();
      setPlans(Array.isArray(response.data) ? response.data : []);
    } catch (error: unknown) {
      console.error('Error fetching plans:', error);
      setError(t('reservationsPage.errorFetchingPlans'));
    }
  }, [t]);

  const fetchBusiness = useCallback(async (): Promise<void> => {
    try {
      const response = await getBusiness();
      setBusiness(response.data ?? null);
    } catch (error: unknown) {
      console.error('Error fetching business:', error);
      setError(t('reservationsPage.errorFetchingBusiness'));
    }
  }, [t]);

  const fetchPendingApprovalReservations = useCallback(async (staffId: string): Promise<void> => {
    try {
      const response = await getPendingApprovalReservations(staffId);
      setPendingApprovalReservations(Array.isArray(response.data) ? response.data : []);
    } catch (error: unknown) {
      console.error('Error fetching pending approval reservations:', error);
      setError(t('reservationsPage.errorFetchingPendingApprovals'));
    }
  }, [t]);

  const fetchPastIncompleteReservations = useCallback(async (): Promise<void> => {
    try {
      // Filter existing reservations for past incomplete ones
      const pastIncomplete = reservations.filter(res =>
        isPast(new Date(res.start)) &&
        res.status === 'confirmed' &&
        (!selectedStylist?._id || res.staffId._id === selectedStylist._id)
      );
      setPastIncompleteReservations(pastIncomplete);
    } catch (error: unknown) {
      console.error('Error filtering past incomplete reservations:', error);
    }
  }, [reservations, selectedStylist]);

  useEffect(() => {
    const fetchAllData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchReservations(),
          fetchStylistReservations(),
          fetchPlans(),
          fetchBusiness()
        ]);
      } catch (error: unknown) {
        console.error('Error fetching all data:', error);
        setError(t('reservationsPage.errorFetchingData'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [fetchReservations, fetchStylistReservations, fetchPlans, fetchBusiness, t]);

  useEffect(() => {
    const currentSearch = searchParams.toString();
    const searchSuffix = currentSearch ? `?${currentSearch}` : '';
    if (routeStylistId) {
      const foundStylist = stylists.find(stylist => stylist._id === routeStylistId);
      setSelectedStylist(foundStylist || stylists[0]);
    } else if (stylists.length > 0) {
      setSelectedStylist(stylists[0]);
      navigate(`${basePath}/${stylists[0]._id}${searchSuffix}`);
    }
  }, [routeStylistId, stylists, navigate, searchParams]);

  useEffect(() => {
    if (selectedStylist?._id) {
      fetchPendingApprovalReservations(selectedStylist._id);
    }
  }, [selectedStylist, fetchPendingApprovalReservations]);

  useEffect(() => {
    fetchPastIncompleteReservations();
  }, [fetchPastIncompleteReservations]);

  const refetchData = useCallback(async (): Promise<void> => {
    await Promise.all([
      fetchReservations(),
      fetchStylistReservations(),
      selectedStylist?._id && fetchPendingApprovalReservations(selectedStylist._id)
    ]);
    fetchPastIncompleteReservations();
  }, [fetchReservations, fetchStylistReservations, fetchPendingApprovalReservations, fetchPastIncompleteReservations, selectedStylist]);

  const handleEdit = (reservation: Reservation): void => {
    setSelectedStylist(stylists.find(s => s._id === reservation.staffId._id) || null);
  };

  const handleApproval = async (reservationId: string): Promise<void> => {
    await approveReservation(reservationId);
    await fetchReservations();
  };

  const handleNoShow = async (reservationId: string): Promise<void> => {
    await noShowReservation(reservationId);
    await fetchReservations();
  };

  const handleComplete = async (reservationId: string): Promise<void> => {
    await completeReservation(reservationId);
    await fetchReservations();
  };

  // Open the confirmation modal and store the reservation ID
  const openCancelConfirmModal = (reservationId: string): void => {
    setReservationToCancel(reservationId);
    setShowCancelConfirmModal(true);
  };

  // Handle the actual cancellation once confirmed
  const handleCancel = async (): Promise<void> => {
    try {
      await cancelReservation(reservationToCancel!, {});
      await fetchReservations();
      setError(null);
      setShowCancelConfirmModal(false); // Close confirmation modal
      setReservationToCancel(null); // Clear the reservation to cancel
    } catch (error: unknown) {
      console.error('Error cancelling reservation:', error);
      setError(t('reservationsPage.errorCancellingReservation'));
    }
  };

  const handleStylistSelected = (value: Stylist | string): void => {
    const stylistId = typeof value === 'string' ? value : value._id;
    const stylist = stylists.find((s) => s._id === stylistId);
    setSelectedStylist(stylist || null);
    navigate(`${basePath}/${stylistId}/${routeFilter || 'future'}`);
  };

  const handleGoogleOAuth = async (): Promise<void> => {
    const frontendUrl = `${import.meta.env.VITE_BASE_URL}${basePath}`;
    googleCalendarAuthRedirect(selectedStylist!._id, frontendUrl);
  };

  const fetchGoogleCalendars = useCallback(async (): Promise<void> => {
    try {
      const calendarsResponse = await listGoogleCalendars(selectedStylist!._id);
      setGoogleCalendars(Array.isArray(calendarsResponse.data) ? calendarsResponse.data : []);
    } catch (error: unknown) {
      console.error('Error fetching Google calendars:', error);
      setError(t('reservationsPage.errorFetchingCalendars'));
    }
  }, [t, selectedStylist]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('action') && searchParams.get('action') === 'calendar-connected' && selectedStylist) {
      setShowCalendarModal(true);
      fetchGoogleCalendars();
    }
  }, [fetchGoogleCalendars, selectedStylist]);

  const handleCalendarSync = async (): Promise<void> => {
    try {
      await syncGoogleEvents(selectedStylist!._id, selectedCalendars);

      // Save the selected calendars to sync
      await saveCalendarsToSync(selectedStylist!._id, selectedCalendars);

      setShowCalendarModal(false);
      // Remove all query parameters from the URL
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
      fetchStylistReservations();
    } catch (error: unknown) {
      console.error('Error syncing Google events:', error);
      setError(t('reservationsPage.errorSyncingEvents'));
    }
  };

  const handleOpenIOSCalendarDialog = (): void => {
    setIOSCalendarUrlError('');
    setIOSCalendarUrlSuccess('');
    setShowIOSCalendarDialog(true);
  };

  const handleIOSCalendarSync = async (): Promise<void> => {
    if (!selectedStylist?._id || !selectedStylist?.iosCalendarUrl) {
      toast.error(t('reservationsPage.noIOSCalendarConfigured'));
      return;
    }

    setIOSSyncLoading(true);
    try {
      await syncIOSCalendar(selectedStylist._id);
      toast.success(t('reservationsPage.iosCalendarSyncInitiated'));
      // Refetch reservations after a short delay to show updated data
      setTimeout(() => {
        fetchStylistReservations();
      }, 3000);
    } catch (error: unknown) {
      console.error('Error syncing iOS calendar:', error);
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.message || apiError.message;

      if (apiError.response?.status === 429) {
        // Rate limit error
        const remainingSeconds = apiError.response?.data?.remainingSeconds;
        toast.error(t('reservationsPage.rateLimitError', { seconds: remainingSeconds || 'a few' }));
      } else {
        toast.error(t('reservationsPage.errorSyncingIOSCalendar') + ': ' + errorMessage);
      }
    } finally {
      setIOSSyncLoading(false);
    }
  };

  const handleSaveIOSCalendarUrl = async (url: string): Promise<void> => {
    setIOSCalendarUrlLoading(true);
    setIOSCalendarUrlError('');
    setIOSCalendarUrlSuccess('');
    try {
      // Ensure empty strings are sent as null, not "null"
      const urlValue = url && url.trim() !== '' ? url.trim() : null;
      await updateStylistDetails({ _id: selectedStylist!._id, iosCalendarUrl: urlValue });
      setIOSCalendarUrlSuccess(t('reservationsPage.iosCalendarDialogSuccess'));
      await fetchStylistReservations();
      setTimeout(() => {
        setShowIOSCalendarDialog(false);
        setIOSCalendarUrlSuccess('');
      }, 1200);
    } catch {
      setIOSCalendarUrlError(t('reservationsPage.iosCalendarDialogError'));
    } finally {
      setIOSCalendarUrlLoading(false);
    }
  };

  const handleReservationModeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const mode = event.target.value;
    if (mode !== selectedStylist?.reservationMode) {
      setNewReservationMode(mode);
      setShowReservationModeConfirmModal(true);
    }
  };

  const handleConfirmReservationModeChange = async (): Promise<void> => {
    if (!selectedStylist || !newReservationMode) return;

    try {
      await updateStylistDetails({ _id: selectedStylist._id, reservationMode: newReservationMode });

      // Fetch the updated list of stylists
      await fetchStylistReservations();

      toast.success(t('reservationsPage.reservationModeUpdated'));
    } catch (err: unknown) {
      console.error('Error updating reservation mode:', err);
      toast.error(t('reservationsPage.errorUpdatingReservationMode'));
    } finally {
      setShowReservationModeConfirmModal(false);
      setNewReservationMode('');
    }
  };

  // Policy action rendered in the page header for easy access.
  const policyAction = (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      startIcon={<PolicyIcon />}
      onClick={() => setShowPolicyModal(true)}
    >
      {t('reservationsPage.policyButton')}
    </Button>
  );

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title={t('reservationsPage.title', 'Reservations')}
          icon={<EventIcon />}
          classicPath="/classic/reservations"
        />
        <LoadingState />
      </PageContainer>
    );
  }

  if (stylists.length === 0) {
    return <NoStylist />;
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('reservationsPage.title', 'Reservations')}
        subtitle={t('reservationsPage.subtitle', 'Manage bookings, approvals and calendar sync.')}
        icon={<EventIcon />}
        action={policyAction}
        classicPath="/classic/reservations"
      />

      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        {/* Stylist + reservation-mode controls */}
        <SectionCard title={t('reservationsPage.stylistSection', 'Stylist')} icon={<EventIcon />}>
          <StylistSelector
            stylists={stylists}
            selectedStylistId={selectedStylist?._id}
            onStylistSelect={handleStylistSelected}
            layout="wrap"
            showEmail={true}
            variant="id"
          />

          {selectedStylist && selectedStylist.lastSyncTime && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t('reservationsPage.lastSynced', 'Last synced')}: {format(new Date(selectedStylist.lastSyncTime), 'HH:mm dd-MM-yyyy')}
            </Typography>
          )}

          {selectedStylist && (
            <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                {t('stylistProfileSettings.reservationMode')}
              </FormLabel>
              <RadioGroup
                row
                aria-label="reservation-mode"
                name="reservationMode"
                value={selectedStylist.reservationMode || 'auto-confirm'}
                onChange={handleReservationModeChange}
              >
                <FormControlLabel
                  value="auto-confirm"
                  control={<Radio />}
                  label={t('stylistProfileSettings.autoConfirm')}
                />
                <FormControlLabel
                  value="need-approval"
                  control={<Radio />}
                  label={t('stylistProfileSettings.needApproval')}
                />
              </RadioGroup>
            </FormControl>
          )}
        </SectionCard>

        {/* Awaiting approval */}
        <SectionCard
          title={t('reservationsPage.awaitingApproval', 'Awaiting approval')}
          icon={<HourglassEmptyIcon />}
          action={
            <Chip
              label={pendingApprovalReservations.length}
              size="small"
              color={pendingApprovalReservations.length > 0 ? 'warning' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          }
        >
          {pendingApprovalReservations.length === 0 ? (
            <EmptyState
              dense
              icon={<HourglassEmptyIcon />}
              title={t('reservationsPage.noPendingApprovals', 'Nothing awaiting approval')}
              description={t('reservationsPage.noPendingApprovalsHint', 'New booking requests needing your approval will appear here.')}
            />
          ) : (
            <AwaitingApprovalList
              reservations={pendingApprovalReservations}
              onRefresh={refetchData}
            />
          )}
        </SectionCard>

        {/* Confirm / complete past incomplete */}
        {pastIncompleteReservations.length > 0 && (
          <SectionCard
            title={t('reservationsPage.confirmCompleted', 'Confirm completed')}
            icon={<EventBusyIcon />}
            action={
              <Chip
                label={pastIncompleteReservations.length}
                size="small"
                color="info"
                sx={{ fontWeight: 700 }}
              />
            }
          >
            <ConfirmCompletedList
              reservations={pastIncompleteReservations}
              onNoShow={handleNoShow}
              onComplete={handleComplete}
              onReviewQr={(reservation) => setReviewQrReservation({ _id: reservation._id, customerName: reservation.customerName })}
              onRefresh={refetchData}
            />
          </SectionCard>
        )}

        {/* Calendar sync */}
        <SectionCard title={t('reservationsPage.calendarSync', 'Calendar sync')} icon={<SyncIcon />}>
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ '& > *': { flex: { xs: '1 1 100%', sm: '0 1 auto' } } }}
          >
            <Button
              variant="contained"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleOAuth}
            >
              {t('reservationsPage.syncWithGoogleCalendar')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AppleIcon />}
              onClick={handleOpenIOSCalendarDialog}
            >
              {t('reservationsPage.syncWithIOSCalendar')}
            </Button>
            {selectedStylist?.iosCalendarUrl && (
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleIOSCalendarSync}
                disabled={iosSyncLoading}
              >
                {iosSyncLoading ? t('reservationsPage.syncing') : t('reservationsPage.manualSyncIOS')}
              </Button>
            )}
          </Stack>

          {/* iOS Calendar Disabled Warning */}
          {selectedStylist && !selectedStylist.iosCalendarUrl && selectedStylist.iosCalendarFailureCount! > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t('reservationsPage.iosCalendarDisabledWarning')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleOpenIOSCalendarDialog}
                sx={{ mt: 1 }}
              >
                {t('reservationsPage.addNewCalendar')}
              </Button>
            </Alert>
          )}
        </SectionCard>

        {/* Reservations list / calendar */}
        <SectionCard
          title={t('reservationsPage.reservationsSection', 'Reservations')}
          icon={<EventIcon />}
          subtitle={t('reservationsPage.reservationsSectionHint', 'Swipe the table sideways on small screens to see all columns.')}
          disableContentPadding
          contentSx={{ p: { xs: 1, sm: 1.5 } }}
        >
          {reservations.length === 0 ? (
            <EmptyState
              icon={<EventBusyIcon />}
              title={t('reservationsPage.noReservations', 'No reservations')}
              description={t('reservationsPage.noReservationsHint', 'When customers book with this stylist, their reservations will show up here.')}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                borderRadius: 2,
                border: `1px solid ${brand.border}`,
                // Give the wide 14-column table room to breathe while
                // keeping it inside a graceful horizontal scroll container.
                '& table': { minWidth: isMobile ? 720 : 'auto' },
              }}
            >
              <ReservationList
                reservations={reservations}
                stylists={stylists}
                business={business!}
                plans={plans}
                onEdit={handleEdit}
                onCancel={openCancelConfirmModal}
                onApproval={handleApproval}
                onNoShow={handleNoShow}
                onComplete={handleComplete}
                refetchData={refetchData}
                stylistId={selectedStylist?._id || ''}
              />
            </Box>
          )}
        </SectionCard>
      </Stack>

      <Modal open={showCancelConfirmModal} onClose={() => setShowCancelConfirmModal(false)}>
        <div>
          <h2>{t('reservationsPage.confirmCancellation')}</h2>
          <p>{t('reservationsPage.cancelReservationPrompt')}</p>
          <Button variant="outlined" onClick={() => setShowCancelConfirmModal(false)}>
            {t('reservationsPage.no')}
          </Button>
          <Button variant="contained" color="error" onClick={handleCancel}>
            {t('reservationsPage.yesCancelReservation')}
          </Button>
        </div>
      </Modal>

      <CalendarSelectionModal
        open={showCalendarModal}
        calendars={googleCalendars}
        selectedCalendars={selectedCalendars}
        onSelect={setSelectedCalendars}
        onClose={() => setShowCalendarModal(false)}
        onSync={handleCalendarSync}
      />

      <Modal open={showReservationModeConfirmModal} onClose={() => setShowReservationModeConfirmModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" component="h2">
            {t('reservationsPage.confirmReservationModeChangeTitle')}
          </Typography>
          <Typography sx={{ mt: 2 }}>
            {t('reservationsPage.confirmReservationModeChangeMessage', { newMode: newReservationMode })}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowReservationModeConfirmModal(false)} sx={{ mr: 1 }}>
              {t('common.cancel')}
            </Button>
            <Button variant="contained" onClick={handleConfirmReservationModeChange}>
              {t('common.confirm')}
            </Button>
          </Box>
        </Box>
      </Modal>

      <QuickReviewModal
        open={!!reviewQrReservation}
        onClose={() => setReviewQrReservation(null)}
        reservation={reviewQrReservation}
      />

      <IOSCalendarUrlDialog
        open={showIOSCalendarDialog}
        initialUrl={selectedStylist?.iosCalendarUrl || ''}
        onClose={() => setShowIOSCalendarDialog(false)}
        onSave={handleSaveIOSCalendarUrl}
        loading={iosCalendarUrlLoading}
        error={iosCalendarUrlError}
        success={iosCalendarUrlSuccess}
      />

      {/* Policy Dialog */}
      <Dialog
        open={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {t('reservationsPage.policyTitle', 'Cancellation Policy')}
          <IconButton
            aria-label="close"
            onClick={() => setShowPolicyModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          <iframe
            src={`${getPolicyUrl()}?_=${Date.now()}`}
            title={t('reservationsPage.policyTitle')}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

const WrappedReservationsPage = withUser(ReservationsPage);
export default WrappedReservationsPage;
